import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { PageType } from 'src/app/models/enum/page-type.enum';
import { Item } from 'src/app/models/item.model';
import { Job } from 'src/app/models/job.model';
import { UserContactInfo } from 'src/app/models/user-contact-info.model';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { LookupService } from 'src/app/services/lookup.service';
import { JobService } from 'src/app/services/sections/job/job.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { ToastService } from 'src/app/services/ui/toast.service';
import { ButtonIcons } from '../../action-button/models/button-icons.enum';
import { ToastType } from '../../toast/models/toast-type.enum';
import { RequestUpdateEntity } from './model/request-update-entity.enum';
import { RequestUpdateNotificationType } from './model/request-update-notification-type.enum';
import { RequestUpdatePayload } from './model/request-update-payload.model';
import { sortBy } from 'lodash-es';
import { ActionsPlaceholderSettings } from 'src/app/models/enum/actions-placeholder-settings.enum';
import { SettingsPreferences } from 'src/app/services/ui/models/settings-preferences.model';
import { RequestUpdateService } from 'src/app/services/sections/job/request-update.service';
import { UserService } from 'src/app/services/sections/shared/user.service';

@Component({
	templateUrl: './request-update.component.html',
	styleUrls: ['./request-update.component.scss', '../../stylesheets/job-actions.scss', '../../stylesheets/job-main.scss', '../../stylesheets/job-inputs.scss', '../../stylesheets/job-contacts.scss']
})
export class RequestUpdateComponent implements OnInit, OnDestroy, AfterViewInit {

	public formModel: FormGroup;
	public contactOptions: UserContactInfo[];

	public entityOptions: Item<number, string>[];
	public typeOptions: Item<number, string>[];

	public unsubscriber$ = new Subject<void>();

	public job: Job = null;
	public customerPreferences: SettingsPreferences;
	public lastNotificationTypeSelectionChoices: Map<number, number>;
	public pageType: PageType;

	public readonly ButtonIcons = ButtonIcons;
	public readonly RequestUpdateEntity = RequestUpdateEntity;
	public readonly RequestUpdateNotificationType = RequestUpdateNotificationType;
	public readonly ActionsPlaceholderSettings = ActionsPlaceholderSettings;

	public isLoadingContacts = false;
	public isLoading = true;

	public self = this;
	public PageType = PageType;

	private assigneeContacts: UserContactInfo[] = [];
	private vendorContacts: UserContactInfo[] = [];

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private userService: UserService,
		private requestUpdateService: RequestUpdateService
	) { }

	ngOnInit(): void {
		this.job = (this.activatedRoute.snapshot.data.jobDetails || {});
		this.customerPreferences = this.activatedRoute.snapshot.data.customerPreferences as SettingsPreferences;
		this.lastNotificationTypeSelectionChoices = (this.activatedRoute.snapshot.data.lastRequestUpdateChoice || []);

		this.pageType = this.activatedRoute.snapshot.data.pageType;

		GlobalSettingsService.isSecondaryLoaderLoading = true;

		this.formModel = new FormGroup({
			entityId: new FormControl(RequestUpdateEntity.Assignee, [Validators.required]),
			typeId: new FormControl(null, [Validators.required]),
			contact: new FormControl(null, [Validators.required]),
			comment: new FormControl(null, [Validators.required, Validators.pattern(/\S+/)])
		});

		this.formModel.get('entityId')
			.valueChanges
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(() => {
				this.isLoadingContacts = true;
				const entityIdControl = this.formModel.get('entityId');
				const contacts$ = this.getContacts(entityIdControl.value);
				contacts$.subscribe(contacts => {
					const contactControl = this.formModel.get('contact');
					this.contactOptions = contacts;

					if (this.contactOptions?.length) {
						contactControl.setValue(this.contactOptions[0]);
					} else {
						contactControl.setValue(null);
					}

					this.isLoadingContacts = false;
				}, (error) => {
					this.isLoadingContacts = false;
					ToastService.pushToast({
						icon: 'error',
						text: `Unable to load the ${entityIdControl.value === RequestUpdateEntity.Assignee ? 'assignees' : 'vendor users'}!`,
						type: ToastType.Error
					});
				});
			});

		this.formModel.get('contact')
			.valueChanges
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe((change: UserContactInfo) => {
				const contactControl = this.formModel.get('contact');
				const typeControl = this.formModel.get('typeId');

				if (contactControl.value) {
					if (change.phoneNumber && !change.emailAddress) {
						typeControl.setValue(RequestUpdateNotificationType.Sms);
					} else if (!change.phoneNumber && change.emailAddress) {
						typeControl.setValue(RequestUpdateNotificationType.Email);
					} else if (this.lastNotificationTypeSelectionChoices.get(change.userAccountId)) {
						typeControl.setValue(this.lastNotificationTypeSelectionChoices.get(change.userAccountId));
					} else {
						typeControl.reset();
					}
				} else {
					typeControl.setValue(null);
				}
			});

		this.prepopulateRecipient();

		this.entityOptions = [
			{ id: RequestUpdateEntity.Assignee, name: 'Assignee' },
			{ id: RequestUpdateEntity.Vendor, name: 'Vendor' }
		];

		this.typeOptions = [
			{ id: RequestUpdateNotificationType.Sms, name: 'SMS' },
			{ id: RequestUpdateNotificationType.Email, name: 'Email' }
		];
	}

	ngAfterViewInit(): void {
		GlobalSettingsService.isGlobalLoaderLoading = false;
	}

	public ngOnDestroy(): void {
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}

	public isTypeControlDisabled(type: Item<number, string>): boolean {
		return type.id === this.formModel.get('typeId').value ||
			!this.formModel.get('contact').value ||
			(type.id === RequestUpdateNotificationType.Sms && !this.formModel.get('contact').value.phoneNumber) ||
			(type.id === RequestUpdateNotificationType.Email && !this.formModel.get('contact').value.emailAddress) ||
			this.isLoadingContacts ||
			this.isLoading;
	}

	public submitRequest(): void {

		GlobalSettingsService.isSecondaryLoaderLoading = true;

		const model = this.formModel.getRawValue();

		const payload: RequestUpdatePayload = {
			actionType: RequestUpdateNotificationType[model.typeId],
			comment: model.comment,
			userAccountId: model.contact.userAccountId
		};

		this.requestUpdateService.requestUpdate(TokenHelper.getToken(this.activatedRoute), payload)
			.subscribe(
				() => {
					GlobalSettingsService.isSecondaryLoaderLoading = false;
					this.router.navigate(['../'], { relativeTo: this.activatedRoute });
				},
				() => {
					GlobalSettingsService.isSecondaryLoaderLoading = false;
				}
			);
	}

	private prepopulateRecipient(): void {

		// Check queryParam 'recipient' for custom prepopulation
		// Search 'recipient' ID through Assignees and Vendors
		// If found, preselect
		// Else preselect first Assignee or first Vendor (if no assignees)
		const recipientId = parseInt(this.activatedRoute.snapshot.queryParamMap.get('recipient'), 10);

		forkJoin([
			this.getContacts(RequestUpdateEntity.Assignee),
			this.getContacts(RequestUpdateEntity.Vendor)
		]).subscribe(([assignees, vendors]) => {
			const entityIdControl = this.formModel.get('entityId');

			let recipient = null;

			if (assignees?.length && recipientId) {
				recipient = assignees.find(item => item.userAccountId === recipientId);

				if (recipient) {
					this.contactOptions = assignees;
					this.formModel.get('contact').setValue(recipient);
				}
			}

			if (!recipient && vendors?.length && recipientId) {
				recipient = vendors.find(item => item.userAccountId === recipientId);

				if (recipient) {
					entityIdControl.setValue(RequestUpdateEntity.Vendor);
					this.contactOptions = vendors;
					this.formModel.get('contact').setValue(recipient);
				}
			}

			if (!recipient) {
				if (assignees?.length) {
					this.contactOptions = assignees;
					this.formModel.get('contact').setValue(assignees[0]);
				} else {
					entityIdControl.setValue(RequestUpdateEntity.Vendor);
					this.contactOptions = vendors;
					this.formModel.get('contact').setValue(vendors[0]);
				}
			}

			this.isLoading = false;
			GlobalSettingsService.isSecondaryLoaderLoading = false;
		});
	}

	private getContacts(entityId: RequestUpdateEntity): Observable<UserContactInfo[]> {
		let contacts$: Observable<UserContactInfo[]> = of(null);
		this.contactOptions = [];
		switch (entityId) {
			case RequestUpdateEntity.Vendor:
				contacts$ = this.userService
					.getVendorUsers(TokenHelper.getToken(this.activatedRoute))
					.pipe(
						takeUntil(this.unsubscriber$),
						map(contacts => (sortBy(contacts, [
							(item: UserContactInfo) => item.lastName.toLowerCase(),
							(item: UserContactInfo) => item.firstName.toLowerCase()
						]))),
						tap(contacts => this.vendorContacts = contacts)
					);

				if (this.vendorContacts?.length) {
					contacts$ = of(this.vendorContacts);
				}

				break;

			case RequestUpdateEntity.Assignee:
				contacts$ = this.userService
					.getAssigneeUsers(TokenHelper.getToken(this.activatedRoute))
					.pipe(
						takeUntil(this.unsubscriber$),
						map(contacts => (sortBy(contacts, [
							(item: UserContactInfo) => item.lastName.toLowerCase(),
							(item: UserContactInfo) => item.firstName.toLowerCase()
						]))),
						tap(contacts => this.assigneeContacts = contacts)
					);

				if (this.assigneeContacts?.length) {
					contacts$ = of(this.assigneeContacts);
				}

				break;

			default: break;
		}

		return contacts$;
	}

}
